import { notification } from "antd";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

import { tokenStorage } from "@/modules/auth";
import { getOrStartRefresh } from "@/modules/auth/sessionRefresh";
import config from "../../config";
import { toError } from "../../utils/error";
import { getFileNameFromContentDisposition } from "../../utils/exportFileName";

declare module "axios" {
  interface AxiosRequestConfig {
    hideErrorCode?: boolean;
    skipNotification?: boolean;
    _isRetryAfterRefresh?: boolean;
  }
}

export const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
  "Accept-Language": "en-GB",
};

const http: AxiosInstance = axios.create({
  baseURL: `${config.VITE_APP_API_URL}/api`,
  headers: { ...DEFAULT_HEADERS },
});

http.interceptors.request.use((config) => {
  if (config.headers) {
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
  }
  return config;
});

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalConfig = error.config as AxiosRequestConfig | undefined;

    /*
     * 401 path: try one silent refresh, then replay. Concurrent 401s
     * collapse into a single refresh via `getOrStartRefresh`. The
     * `_isRetryAfterRefresh` flag prevents loops if the replay itself
     * 401s — that case falls through to the normal error notification.
     */
    if (
      status === 401 &&
      originalConfig &&
      !originalConfig._isRetryAfterRefresh
    ) {
      try {
        await getOrStartRefresh();
      } catch {
        /*
         * Refresh failed — getOrStartRefresh has already emitted the
         * session:expired event (when the refresh token itself is
         * rejected) and the modal listener will take it from here.
         * Rethrow the ORIGINAL error so callers see the 401 they made.
         */
        throw error;
      }
      originalConfig._isRetryAfterRefresh = true;
      return http.request(originalConfig);
    }

    const notify = originalConfig?.skipNotification
      ? () => null
      : notification.error;

    if (axios.isAxiosError(error) && originalConfig?.hideErrorCode) {
      notify({ message: error.response?.data?.message });
      throw error;
    }

    if (status === 401) {
      notify({ message: "Unauthorized. Please log in again." });
      throw error;
    }

    notify({
      message: `An error occurred: "${error.message} (${error.response?.data?.message})"`,
    });
    throw error;
  }
);

/**
 * The error every `BaseApi` call rejects with. `isResponseMissing` tells a
 * request the server never answered (network error, lost response) from one it
 * refused, which a caller needs before it repeats a request that may already
 * have taken effect.
 */
export class ApiError extends Error {
  constructor(message: string, readonly isResponseMissing = false) {
    super(message);
  }
}

const convertToApiError = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    return new ApiError(
      error.response?.data?.message || error.message,
      !error.response
    );
  }
  return toError(error);
};

export default abstract class BaseApi {
  protected abstract get moduleUrl(): string;

  protected async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return await http.get(`${this.moduleUrl}${url}`, config);
    } catch (error) {
      throw convertToApiError(error);
    }
  }

  protected async download(
    url: string,
    fileName: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    try {
      const response = await http.get(`${this.moduleUrl}${url}`, config);

      const href = URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = href;
      // Prefer the name the backend sends; `fileName` is the fallback.
      link.setAttribute(
        "download",
        getFileNameFromContentDisposition(
          response.headers?.["content-disposition"]
        ) ?? fileName
      );
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
      return;
    } catch (error) {
      throw convertToApiError(error);
    }
  }

  protected async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return await http.post(`${this.moduleUrl}${url}`, data, config);
    } catch (error) {
      throw convertToApiError(error);
    }
  }

  protected async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return await http.put(`${this.moduleUrl}${url}`, data, config);
    } catch (error) {
      throw convertToApiError(error);
    }
  }

  protected async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return await http.patch(`${this.moduleUrl}${url}`, data, config);
    } catch (error) {
      throw convertToApiError(error);
    }
  }

  protected async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return await http.delete(`${this.moduleUrl}${url}`, config);
    } catch (error) {
      throw convertToApiError(error);
    }
  }
}
